import { useUser } from '@clerk/clerk-react'
import { useState, useEffect } from 'react'
import Header from '../components/Header'

interface DashboardStats {
  ordersToday: number
  revenueToday: number
  pendingOrders: number
  cancelledToday: number
  totalOrders: number
  salesSummary: {
    totalOrders: number
    totalQty: number
    totalBuyerPayment: number
    totalOmset: number
    totalDiscount: number
    totalCommission: number
    totalServiceFee: number
  }
}

interface BotStatus {
  status: string
  lastRun?: string
  nextRun?: string
  error?: string
}

export default function Dashboard() {
  const { user } = useUser()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch dashboard stats
      const statsResponse = await fetch('/api/dashboard/stats')
      if (!statsResponse.ok) throw new Error('Failed to fetch stats')
      const statsData = await statsResponse.json()
      setStats(statsData)

      // Fetch bot status
      const botResponse = await fetch('/api/bot/status')
      if (botResponse.ok) {
        const botData = await botResponse.json()
        setBotStatus(botData)
      }

      setError(null)
    } catch (err) {
      console.error('Dashboard fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }

  const triggerBotAction = async (action: string) => {
    try {
      const response = await fetch('/api/bot/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      })

      if (!response.ok) throw new Error('Failed to trigger action')
      
      const result = await response.json()
      alert(`✅ ${result.message}`)
      
      // Refresh data after action
      setTimeout(fetchDashboardData, 2000)
    } catch (err) {
      console.error('Trigger error:', err)
      alert(`❌ Failed to trigger ${action}`)
    }
  }

  useEffect(() => {
    fetchDashboardData()
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  if (loading && !stats) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Loading Dashboard...</h1>
      </div>
    )
  }

  if (error && !stats) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Dashboard Error</h1>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={fetchDashboardData}>Retry</button>
      </div>
    )
  }

  return (
    <div>
      <Header 
        title="Dashboard"
        subtitle={`Welcome back, ${user?.firstName}! Here's your business overview.`}
        actions={
          <>
            <button 
              onClick={() => triggerBotAction('daily-pipeline')}
              style={{ 
                padding: '0.5rem 1rem', 
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              🔄 Run Pipeline
            </button>
            <button 
              onClick={() => triggerBotAction('sales-report')}
              style={{ 
                padding: '0.5rem 1rem', 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              📊 Sales Report
            </button>
            <button 
              onClick={fetchDashboardData}
              style={{ 
                padding: '0.5rem 1rem', 
                background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh
            </button>
          </>
        }
      />
      
      <div style={{ padding: '2rem' }}>

      {/* Bot Status */}
      {botStatus && (
        <div style={{ 
          padding: '1rem', 
          background: botStatus.status === 'running' ? '#d4edda' : '#f8d7da', 
          borderRadius: '8px', 
          marginBottom: '2rem',
          border: `1px solid ${botStatus.status === 'running' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <h3>🤖 Bot Status: {botStatus.status}</h3>
          {botStatus.lastRun && <p>Last Run: {new Date(botStatus.lastRun).toLocaleString()}</p>}
          {botStatus.nextRun && <p>Next Run: {new Date(botStatus.nextRun).toLocaleString()}</p>}
          {botStatus.error && <p style={{ color: 'red' }}>Error: {botStatus.error}</p>}
        </div>
      )}

      {/* Main Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}
      >
        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>📦 Orders Today</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>
            {stats?.ordersToday || 0}
          </p>
        </div>

        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>💰 Revenue Today</h3>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
            {formatCurrency(stats?.revenueToday || 0)}
          </p>
        </div>

        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>⏳ Pending Orders</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
            {stats?.pendingOrders || 0}
          </p>
        </div>

        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>❌ Cancelled Today</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>
            {stats?.cancelledToday || 0}
          </p>
        </div>
      </div>

      {/* Detailed Sales Summary */}
      {stats?.salesSummary && (
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>📈 Today's Sales Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <p><strong>Total Orders:</strong> {stats.salesSummary.totalOrders}</p>
              <p><strong>Total Quantity:</strong> {stats.salesSummary.totalQty}</p>
            </div>
            <div>
              <p><strong>Buyer Payment:</strong> {formatCurrency(stats.salesSummary.totalBuyerPayment)}</p>
              <p><strong>Total Omset:</strong> {formatCurrency(stats.salesSummary.totalOmset)}</p>
            </div>
            <div>
              <p><strong>Total Discount:</strong> {formatCurrency(stats.salesSummary.totalDiscount)}</p>
              <p><strong>Commission Fee:</strong> {formatCurrency(stats.salesSummary.totalCommission)}</p>
            </div>
            <div>
              <p><strong>Service Fee:</strong> {formatCurrency(stats.salesSummary.totalServiceFee)}</p>
              <p><strong>Net Profit:</strong> {formatCurrency(
                stats.salesSummary.totalOmset - stats.salesSummary.totalCommission - stats.salesSummary.totalServiceFee
              )}</p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#6c757d' }}>
          Refreshing data...
        </div>
      )}
      </div>
    </div>
  )
}
